import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Application } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { Card, CardContent } from "../ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import ApplicationsTable from "./ApplicationsTable";
import { SortConfig } from "../ui/paginated-table";

const Applications: React.FC = () => {
  const { apiKey } = usePermissions();
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig<Application>>({ key: null, direction: "desc" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<number | null>(null);
  const pageSize = 10;

  const navigate = useNavigate();

  useEffect(() => {
    refreshApplications();
  }, [apiKey]);

  const refreshApplications = () => {
    ApplicationService.getApplications(setIsLoading, apiKey).subscribe(
      (apps: Application[]) => {
        setApplications(apps);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  const handleSort = (key: keyof Application) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleDelete = (applicationId: number) => {
    setDeleteDialogOpen(applicationId);
  };

  const confirmDelete = (applicationId: number) => {
    setIsLoading(true);
    ApplicationService.deleteApplication(applicationId, setIsLoading, apiKey).subscribe(
      () => {
        setApplications((prevApplications) =>
          prevApplications.filter((app) => app.id !== applicationId)
        );
        setDeleteDialogOpen(null);
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };



  return (
    <div className="min-h-screen bg-background">
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Applications</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Applications
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage your applications and their settings
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <ApplicationsTable
          applications={applications}
          loading={isLoading}
          sortConfig={sortConfig}
          onSort={handleSort}
          onDelete={handleDelete}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
        />

        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen !== null && (
          <AlertDialog open={deleteDialogOpen !== null} onOpenChange={(open) => setDeleteDialogOpen(open ? deleteDialogOpen : null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-gc-med">
                  Delete Application
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this application? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteDialogOpen && confirmDelete(deleteDialogOpen)}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};

export default Applications;
